import { assert, executeOnce, objectEntries, type ReadonlyRecord } from '../../../utils'

import {
    type AuthenticationRequest,
    type AuthenticationStatus,
    type AuthenticationWithBackupcode,
    type AuthenticationWithOtpRequest,
    ConnectwareError,
    ConnectwareErrorType,
    InvalidOtpError,
    MfaRequiredError,
    MfaSetupRequiredError,
    MfaTokenExpiredError,
    SessionFromTokenNotFoundError,
    SSOAuthenticationMethod,
} from '../../../domain'

import type { AuthenticationResponse, AuthenticationService, SessionResponse } from '../../../application'

import type { BackendDefinition, BackendMethodPath } from '../../Connectware'
import { extractValidatedJson } from '../Base'
import { ConnectwareHTTPCapabilitiesService } from './Capabilities'
import { type CapabilityCheckers, mapBackupCodeBodyRequest, mapLoginResponse, mapOtpBodyRequest, mapSessionResponse } from './mappers'
import type { MinimalLoginResponse, MinimalSessionResponse } from './Types'
import { isMinimalInvalidOTPResponse, isMinimalLoginResponse, isMinimalSessionResponse, isMinimalUserBannedResponse } from './Validators'

const createIgnorer =
    <T>(value: T): (() => T) =>
    () =>
        value
const ignoreOrchestratorError = createIgnorer(null)
const ignoreMfaError = createIgnorer(false)

export class ConnectwareHTTPAuthenticationService extends ConnectwareHTTPCapabilitiesService implements AuthenticationService {
    private readonly baseURL: string

    private readonly ssoUrlLoginPaths: ReadonlyRecord<SSOAuthenticationMethod, BackendMethodPath<'get'>> = {
        [SSOAuthenticationMethod.MICROSOFT_ENTRA_ID]: '/api/msEntraId/login',
    }

    private readonly ssoFetchers: ReadonlyRecord<SSOAuthenticationMethod, () => Promise<boolean>> = {
        [SSOAuthenticationMethod.MICROSOFT_ENTRA_ID]: () => this.fetchEntraStatus(),
    }

    /**
     * These are preflight checks to validate how Connectware is running
     * They assist the service implementation mappers to drop certain capabilities in case the system can not
     * provide them
     */
    protected readonly capabilitiesCheckers = {
        orchestrator: executeOnce((token: string) =>
            this.request<'/api/containers/orchestrator', 'GET', BackendDefinition<'container-manager', 'Orchestrator'> | null>({
                capability: null,
                method: 'GET',
                path: '/api/containers/orchestrator',
                authenticate: token,
                handlers: {
                    200: (response) => response.getJson().then(({ orchestrator }) => orchestrator),
                    403: ignoreOrchestratorError,
                    503: ignoreOrchestratorError,
                },
            })
        ),
        mfa: executeOnce((token: string) =>
            this.request<'/api/auth/mfa', 'GET', boolean>({
                capability: null,
                method: 'GET',
                path: '/api/auth/mfa',
                authenticate: token,
                handlers: {
                    /**
                     * It should return a boolean because
                     * 'unavailableMfaConfiguration' in src/infrastructure/Connectware/Capabilities.ts
                     * handles only two cases (true/false).
                     * If, for some reason, 'enabled' returns something other than a Boolean (e.g., null or undefined),
                     * then MFA could mistakenly be activated.
                     * */
                    200: (response) => response.getJson().then(({ enabled }) => Boolean(enabled)),
                    403: ignoreMfaError,
                    503: ignoreMfaError,
                },
            })
        ),
    }

    constructor (...[options]: ConstructorParameters<typeof ConnectwareHTTPCapabilitiesService>) {
        super(options)

        this.baseURL = options.baseURL
    }

    private createAuthenticationCapabilityCheckers (token: string): CapabilityCheckers {
        return { orchestrator: () => this.capabilitiesCheckers.orchestrator(token), isMfaActive: () => this.capabilitiesCheckers.mfa(token) }
    }

    private mfaLogin ({ secret, username, ...request }: AuthenticationWithOtpRequest | AuthenticationWithBackupcode): Promise<MinimalLoginResponse> {
        return this.request<'/api/mfa/login', 'POST', MinimalLoginResponse>({
            capability: null,
            method: 'POST',
            path: '/api/mfa/login',
            body: { secret, username, ...('backupCode' in request ? mapBackupCodeBodyRequest(request.backupCode) : mapOtpBodyRequest(request.otp)) },
            handlers: {
                200: (response) => extractValidatedJson(response, isMinimalLoginResponse),
                400: () => new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'There were issues while authenticating the user'),
                401: async (response) => {
                    const json = await extractValidatedJson(response, isMinimalInvalidOTPResponse)

                    if (json.error === 'invalid') {
                        return new MfaTokenExpiredError()
                    }

                    if (json.error === 'wrong OTP') {
                        return new InvalidOtpError({ minutesUntilRetry: json.banDurationMinutes, triesLeft: json.triesLeft })
                    }

                    return new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'Invalid backupCode')
                },
                403: async (response) => {
                    const { minutesUntilBanEnd } = await extractValidatedJson(response, isMinimalUserBannedResponse)
                    return new InvalidOtpError({ minutesUntilRetry: minutesUntilBanEnd, triesLeft: null })
                },
                500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, '500 Internal Server Error'),
            },
        })
    }

    private login (request: AuthenticationRequest): Promise<MinimalLoginResponse> {
        return this.request<'/api/login', 'POST', MinimalLoginResponse>({
            capability: null,
            method: 'POST',
            path: '/api/login',
            body: request,
            handlers: {
                200: (response) => extractValidatedJson(response, isMinimalLoginResponse),
                400: () => new ConnectwareError(ConnectwareErrorType.BAD_REQUEST, 'Unexpected bad request detected while attempting to login'),
                401: () => new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'There were issues while authenticating the user'),
                403: () => new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'There were issues while authenticating the user'),
            },
        })
    }

    private fetchBackendSession (token: string): Promise<MinimalSessionResponse> {
        return this.request<'/api/session', 'GET', MinimalSessionResponse>({
            capability: null,
            method: 'GET',
            path: '/api/session',
            authenticate: token,
            handlers: {
                200: (response) => extractValidatedJson(response, isMinimalSessionResponse),
                401: () => new SessionFromTokenNotFoundError(),
                403: () => new SessionFromTokenNotFoundError(),
            },
        })
    }

    async authenticate (request: AuthenticationRequest | AuthenticationWithOtpRequest | AuthenticationWithBackupcode): Promise<AuthenticationResponse> {
        const backendResponse = await ('secret' in request ? this.mfaLogin(request) : this.login(request))

        if ('needsMfa' in backendResponse) {
            throw new MfaRequiredError(backendResponse.secret)
        }

        const loginResponse = await mapLoginResponse(backendResponse, this.createAuthenticationCapabilityCheckers(backendResponse.token))
        assert(!('enforceMFAEnrollment' in backendResponse), new MfaSetupRequiredError(loginResponse))

        return loginResponse
    }

    fetchSession (token: string): Promise<SessionResponse> {
        return this.fetchBackendSession(token).then((response) => mapSessionResponse(response, this.createAuthenticationCapabilityCheckers(token)))
    }

    fetchAuthenticationStatus (token: string): Promise<AuthenticationStatus> {
        return this.fetchBackendSession(token).then(({ mfa: { enabled, enforced } }) => ({ isMfaEnabled: enabled, isMfaRequired: enforced }))
    }

    async fetchActiveSSOAuthenticationMethods (): Promise<SSOAuthenticationMethod[]> {
        const enabledSSOTypes = await Promise.all(
            objectEntries(this.ssoFetchers).map(([type, fetchIsEnabled]) => fetchIsEnabled().then((enabled) => (enabled ? [type] : [])))
        )
        return enabledSSOTypes.flat()
    }

    fetchSSOAuthenticationUrl (type: SSOAuthenticationMethod): Promise<URL> {
        return Promise.resolve(new URL(this.ssoUrlLoginPaths[type], this.baseURL))
    }

    flushAuthentication (token: string): Promise<void> {
        return this.request<'/api/logout', 'POST', void>({
            capability: null,
            method: 'POST',
            path: '/api/logout',
            body: { token },
            handlers: {
                200: () => Promise.resolve(),
                400: () => new ConnectwareError(ConnectwareErrorType.BAD_REQUEST, 'Bad request while logging out detected'),
                404: () => new ConnectwareError(ConnectwareErrorType.NOT_FOUND, 'User for logout not found'),
            },
        })
    }
}
